import { useState, useEffect, useCallback } from "react";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { ChevronLeft, ChevronRight, ShoppingBag } from "lucide-react";
import { useHeroSlides } from "@/hooks/useHeroSlides";
import { cn } from "@/lib/utils";
import { Skeleton } from "@/components/ui/skeleton";
import { OptimizedImage } from "@/components/ui/optimized-image";

const HeroSlider = () => {
  const { data: slides, isLoading } = useHeroSlides();
  const [currentIndex, setCurrentIndex] = useState(0);
  const [animationKey, setAnimationKey] = useState(0);
  const [isAutoPlaying, setIsAutoPlaying] = useState(true);

  const currentSlide = slides?.[currentIndex];

  const goToSlide = useCallback((index: number) => {
    setCurrentIndex(index);
    setAnimationKey((prev) => prev + 1);
  }, []);

  const goToNext = useCallback(() => {
    if (!slides?.length) return;
    goToSlide((currentIndex + 1) % slides.length);
  }, [currentIndex, slides?.length, goToSlide]);

  const goToPrev = useCallback(() => {
    if (!slides?.length) return;
    goToSlide((currentIndex - 1 + slides.length) % slides.length);
  }, [currentIndex, slides?.length, goToSlide]);

  // Auto-play
  useEffect(() => {
    if (!isAutoPlaying || !slides?.length || slides.length <= 1) return;

    const timer = setInterval(() => {
      goToNext();
    }, 6000);

    return () => clearInterval(timer);
  }, [isAutoPlaying, slides?.length, goToNext]);

  // Pause auto-play on user interaction
  const handleUserInteraction = useCallback(() => {
    setIsAutoPlaying(false);
    // Resume after 10 seconds
    const timer = setTimeout(() => setIsAutoPlaying(true), 10000);
    return () => clearTimeout(timer);
  }, []);

  if (isLoading) {
    return (
      <section className="relative h-[500px] sm:h-[600px] lg:h-[700px] overflow-hidden bg-muted">
        <Skeleton className="absolute inset-0" />
        <div className="container relative z-10 h-full flex items-center">
          <div className="max-w-xl space-y-4">
            <h1 className="sr-only">Gordon Store - Productos Premium para Mascotas en Costa Rica</h1>
            <Skeleton className="h-12 w-3/4" />
            <Skeleton className="h-6 w-full" />
            <Skeleton className="h-12 w-40" />
          </div>
        </div>
      </section>
    );
  }

  if (!slides?.length || !currentSlide) {
    return null;
  }

  return (
    <section 
      className="relative h-[500px] sm:h-[600px] lg:h-[700px] overflow-hidden"
      onMouseEnter={handleUserInteraction}
      onTouchStart={handleUserInteraction}
    >
      {/* Background Image - Layer 1 */}
      <div
        key={`bg-${animationKey}`}
        className="absolute inset-0 animate-hero-bg-enter"
      >
        <OptimizedImage
          src={currentSlide.background_image_url}
          alt="Banner principal - Gordon Store productos para mascotas"
          className="w-full h-full object-cover"
          containerClassName="w-full h-full"
          priority={true}
          useWebP={true}
        />
        {/* Overlay for better text readability */}
        <div className="absolute inset-0 bg-gradient-to-r from-background/90 via-background/60 to-transparent" />
      </div>

      {/* Foreground Image - Layer 2 */}
      {currentSlide.foreground_image_url && (
        <div
          key={`fg-${animationKey}`}
          className={cn(
            "absolute bottom-0 right-0 lg:right-10 xl:right-20",
            "animate-hero-fg-enter",
            "opacity-0 [animation-fill-mode:forwards]"
          )}
          style={{ animationDelay: "300ms" }}
        >
          <OptimizedImage
            src={currentSlide.foreground_image_url}
            alt={currentSlide.foreground_image_alt || "Mascota destacada - Gordon Store"}
            className="h-[300px] sm:h-[400px] lg:h-[550px] xl:h-[600px] w-auto object-contain drop-shadow-2xl"
            containerClassName="h-full"
            priority={true}
            useWebP={true}
          />
        </div>
      )}

      {/* Content - Layer 3 */}
      <div className="container relative z-20 h-full flex items-center">
        <div
          key={`text-${animationKey}`}
          className={cn(
            "max-w-xl lg:max-w-2xl space-y-6",
            "animate-hero-text-enter",
            "opacity-0 [animation-fill-mode:forwards]"
          )}
          style={{ animationDelay: "500ms" }}
        >
          <h1 className="text-4xl sm:text-5xl lg:text-6xl xl:text-7xl font-bold text-foreground leading-tight">
            {currentSlide.title}
          </h1>
          {currentSlide.subtitle && (
            <p className="text-lg sm:text-xl text-muted-foreground max-w-lg">
              {currentSlide.subtitle}
            </p>
          )}
          <div className="flex flex-wrap gap-4 pt-2">
            <Button asChild size="lg" className="text-lg px-8 rounded-full">
              <Link to={currentSlide.button_link}>
                <ShoppingBag className="mr-2 h-5 w-5" />
                {currentSlide.button_text}
              </Link>
            </Button>
          </div>
        </div>
      </div>

      {/* Navigation Arrows */}
      {slides.length > 1 && (
        <>
          <Button
            variant="ghost"
            size="icon"
            onClick={() => {
              handleUserInteraction();
              goToPrev();
            }}
            className={cn(
              "absolute left-4 top-1/2 -translate-y-1/2 z-30",
              "h-12 w-12 rounded-full",
              "bg-background/30 backdrop-blur-sm",
              "hover:bg-background/50 hover:scale-105",
              "transition-all duration-200",
              "text-foreground"
            )}
          >
            <ChevronLeft className="h-6 w-6" />
          </Button>

          <Button
            variant="ghost"
            size="icon"
            onClick={() => {
              handleUserInteraction();
              goToNext();
            }}
            className={cn(
              "absolute right-4 top-1/2 -translate-y-1/2 z-30",
              "h-12 w-12 rounded-full",
              "bg-background/30 backdrop-blur-sm",
              "hover:bg-background/50 hover:scale-105",
              "transition-all duration-200",
              "text-foreground"
            )}
          >
            <ChevronRight className="h-6 w-6" />
          </Button>
        </>
      )}

      {/* Dot Indicators */}
      {slides.length > 1 && (
        <div className="absolute bottom-6 left-1/2 -translate-x-1/2 z-30 flex gap-2">
          {slides.map((_, index) => (
            <button
              key={index}
              onClick={() => {
                handleUserInteraction();
                goToSlide(index);
              }}
              className={cn(
                "h-3 rounded-full transition-all duration-300",
                index === currentIndex
                  ? "bg-primary w-8"
                  : "bg-foreground/30 hover:bg-foreground/50 w-3"
              )}
              aria-label={`Ir al slide ${index + 1}`}
            />
          ))}
        </div>
      )}
    </section>
  );
};

export default HeroSlider;
